import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Plus, 
  BarChart3,
  Activity,
  Users,
  Award,
  Calendar
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRole } from '@/hooks/useRole';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface KPI {
  id: string;
  name: string;
  description: string;
  category: string;
  metric_type: string;
  target_value: number;
  measurement_frequency: string;
  is_active: boolean;
}

interface KPIProgress {
  kpi: KPI;
  current_value: number;
  progress_percentage: number;
  trend: 'up' | 'down' | 'stable';
  meetings_contributing: number;
}

interface MeetingKPIProps {
  selectedPeriod?: string;
  selectedUser?: string;
}

export function MeetingKPITracker({ selectedPeriod = 'current', selectedUser }: MeetingKPIProps) {
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [kpiProgress, setKpiProgress] = useState<KPIProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedKPI, setSelectedKPI] = useState<KPI | null>(null);
  const [kpiValue, setKpiValue] = useState('');

  const targetUserId = selectedUser || user?.id;

  useEffect(() => {
    loadKPIs();
  }, []);

  useEffect(() => {
    if (kpis.length > 0 && targetUserId) {
      calculateKPIProgress();
    }
  }, [kpis, selectedPeriod, targetUserId]);

  const loadKPIs = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user?.id)
        .single();

      let query = supabase
        .from('district_kpis')
        .select('*')
        .eq('is_active', true)
        .order('category');

      if (profile?.district_id) {
        query = query.eq('district_id', profile.district_id);
      }

      const { data, error } = await query;
      if (error) throw error;
      setKpis(data || []);
    } catch (error) {
      console.error('Error loading KPIs:', error);
      toast.error('Failed to load KPIs');
    }
  };

  const calculateKPIProgress = async () => {
    if (!targetUserId) return;

    setLoading(true);
    try {
      let startDate, endDate;
      
      switch (selectedPeriod) {
        case 'current':
          startDate = startOfMonth(new Date());
          endDate = endOfMonth(new Date());
          break;
        case 'last':
          startDate = startOfMonth(subMonths(new Date(), 1));
          endDate = endOfMonth(subMonths(new Date(), 1));
          break;
        case 'quarter':
          startDate = startOfMonth(subMonths(new Date(), 3));
          endDate = endOfMonth(new Date());
          break;
        case 'year':
          startDate = startOfMonth(subMonths(new Date(), 12));
          endDate = endOfMonth(new Date());
          break;
        default:
          startDate = startOfMonth(new Date());
          endDate = endOfMonth(new Date());
      }

      // Get meetings for the period
      const { data: meetings, error: meetingsError } = await supabase
        .from('meetings')
        .select('*')
        .or(`observer_id.eq.${targetUserId},observee_id.eq.${targetUserId}`)
        .eq('status', 'completed')
        .gte('scheduled_date', startDate.toISOString())
        .lte('scheduled_date', endDate.toISOString());

      if (meetingsError) throw meetingsError;

      const progress: KPIProgress[] = kpis.map(kpi => {
        let currentValue = 0;
        let meetingsContributing = 0;

        // Calculate KPI values based on meeting data
        switch (kpi.category) {
          case 'performance':
            // Calculate based on meeting completion rate and duration
            if (kpi.name.toLowerCase().includes('completion')) {
              currentValue = meetings?.length || 0;
              meetingsContributing = meetings?.length || 0;
            } else if (kpi.name.toLowerCase().includes('duration')) {
              const totalMinutes = meetings?.reduce((sum, m) => sum + (m.duration_minutes || 0), 0) || 0;
              currentValue = meetings?.length > 0 ? totalMinutes / meetings.length : 0;
              meetingsContributing = meetings?.length || 0;
            }
            break;

          case 'engagement':
            // Calculate based on mentoring/coaching meetings
            const engagementMeetings = meetings?.filter(m => 
              m.meeting_type === 'mentoring' || m.meeting_type === 'coaching'
            ) || [];
            currentValue = engagementMeetings.length;
            meetingsContributing = engagementMeetings.length;
            break;

          case 'effectiveness':
            // Calculate based on follow-up completion rate
            const followUpRequired = meetings?.filter(m => m.follow_up_required) || [];
            const followUpCompleted = meetings?.filter(m => 
              m.follow_up_required && m.follow_up_date && new Date(m.follow_up_date) <= new Date()
            ) || [];
            currentValue = followUpRequired.length > 0 
              ? (followUpCompleted.length / followUpRequired.length) * 100 
              : 100;
            meetingsContributing = followUpRequired.length;
            break;

          case 'satisfaction':
            // This would typically come from survey data, but for now use meeting frequency
            const totalMeetings = meetings?.length || 0;
            currentValue = totalMeetings;
            meetingsContributing = totalMeetings;
            break;

          default:
            currentValue = meetings?.length || 0;
            meetingsContributing = meetings?.length || 0;
        }

        const progressPercentage = kpi.target_value > 0 
          ? Math.min((currentValue / kpi.target_value) * 100, 100)
          : 0;

        // Simple trend calculation (would be better with historical data)
        const trend: 'up' | 'down' | 'stable' = progressPercentage >= 80 ? 'up' : 
                     progressPercentage >= 50 ? 'stable' : 'down';

        return {
          kpi,
          current_value: currentValue,
          progress_percentage: progressPercentage,
          trend,
          meetings_contributing: meetingsContributing
        };
      });

      setKpiProgress(progress);
    } catch (error) {
      console.error('Error calculating KPI progress:', error);
      toast.error('Failed to calculate KPI progress');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons = {
      performance: Activity,
      satisfaction: Award,
      engagement: Users,
      effectiveness: TrendingUp
    };
    return icons[category as keyof typeof icons] || Target;
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      performance: 'bg-blue-100 text-blue-800 border-blue-200',
      satisfaction: 'bg-green-100 text-green-800 border-green-200',
      engagement: 'bg-purple-100 text-purple-800 border-purple-200',
      effectiveness: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatValue = (value: number, metricType: string) => {
    switch (metricType) {
      case 'percentage':
        return `${value.toFixed(1)}%`;
      case 'numeric':
        return value.toFixed(0);
      case 'scale':
        return `${value.toFixed(1)}/5`;
      default:
        return value.toFixed(1);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            KPI Tracking - {selectedPeriod === 'current' ? 'Current Month' : 
                           selectedPeriod === 'last' ? 'Last Month' :
                           selectedPeriod === 'quarter' ? 'Last 3 Months' : 'Last 12 Months'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">
              Calculating KPI progress...
            </div>
          ) : kpiProgress.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {kpiProgress.map((progress) => {
                const CategoryIcon = getCategoryIcon(progress.kpi.category);
                const TrendIcon = progress.trend === 'up' ? TrendingUp : 
                                 progress.trend === 'down' ? TrendingDown : Activity;
                
                return (
                  <Card key={progress.kpi.id} className="relative">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <CategoryIcon className="h-4 w-4" />
                            <h3 className="font-medium text-sm">{progress.kpi.name}</h3>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {progress.kpi.description}
                          </p>
                        </div>
                        <TrendIcon className={`h-4 w-4 ${
                          progress.trend === 'up' ? 'text-green-500' :
                          progress.trend === 'down' ? 'text-red-500' : 'text-gray-500'
                        }`} />
                      </div>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span>Progress</span>
                          <span className={getProgressColor(progress.progress_percentage)}>
                            {progress.progress_percentage.toFixed(1)}%
                          </span>
                        </div>
                        <Progress value={progress.progress_percentage} className="h-2" />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Current</div>
                          <div className="font-medium">
                            {formatValue(progress.current_value, progress.kpi.metric_type)}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Target</div>
                          <div className="font-medium">
                            {formatValue(progress.kpi.target_value, progress.kpi.metric_type)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className={getCategoryColor(progress.kpi.category)}>
                          {progress.kpi.category}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {progress.meetings_contributing} meetings
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Target className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No KPIs Available</h3>
              <p className="text-muted-foreground mb-6">
                No active KPIs found for your district. Contact your administrator to set up KPIs.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPI Summary */}
      {kpiProgress.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              KPI Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {kpiProgress.filter(p => p.progress_percentage >= 80).length}
                </div>
                <div className="text-sm text-muted-foreground">On Target</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">
                  {kpiProgress.filter(p => p.progress_percentage >= 50 && p.progress_percentage < 80).length}
                </div>
                <div className="text-sm text-muted-foreground">In Progress</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {kpiProgress.filter(p => p.progress_percentage < 50).length}
                </div>
                <div className="text-sm text-muted-foreground">Needs Attention</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {kpiProgress.reduce((sum, p) => sum + p.meetings_contributing, 0)}
                </div>
                <div className="text-sm text-muted-foreground">Total Meetings</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}